"use client";

import { useEffect, useMemo } from "react";
import {
  Activity,
  ArrowUpRight,
  BookOpen,
  Check,
  ClipboardCopy,
  ExternalLink,
  FileDown,
  FileText,
  Gavel,
  HandCoins,
  History,
  Inbox,
  ListChecks,
  Printer,
  RefreshCw,
  Route,
  Search,
} from "lucide-react";
import {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from "@/components/ui/command";
import { useToast } from "@/hooks/use-toast";
import { buildMissionReport, downloadReport } from "@/components/mission/report";
import type { MissionData } from "@/types/mission";

/**
 * Global command palette (Ctrl/Cmd+K or "k").
 * Searches across the whole live mission state — tabs, opportunities,
 * evidence log, decisions, research notes — and offers quick clipboard
 * actions. Read-only against mission data; never mutates anything.
 */

export const TAB_ORDER = [
  "pipeline",
  "earnings",
  "timeline",
  "evidence",
  "next",
  "decisions",
  "research",
] as const;

export type TabValue = (typeof TAB_ORDER)[number];

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  data: MissionData | null;
  onNavigate: (tab: TabValue) => void;
  onRefresh: () => void;
}

export function CommandPalette({ open, onOpenChange, data, onNavigate, onRefresh }: CommandPaletteProps) {
  const { toast } = useToast();

  const go = (tab: TabValue) => {
    onNavigate(tab);
    onOpenChange(false);
  };

  const copy = async (label: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({ title: `${label} copied`, description: `${text.slice(0, 24)}…${text.slice(-8)}` });
    } catch {
      toast({ title: "Copy failed", description: "Clipboard unavailable in this context" });
    }
    onOpenChange(false);
  };

  const openExternal = (url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
    onOpenChange(false);
  };

  /* prebuilt searchable lists (memo on data identity) */
  const opportunities = useMemo(
    () =>
      (data?.pipeline ?? []).map((o) => ({
        id: o.id,
        name: o.name,
        status: o.status,
        category: o.category,
      })),
    [data],
  );
  const decisions = useMemo(() => (data?.decisions ?? []).map((d) => ({ id: d.id, title: d.title })), [data]);
  const recentActions = useMemo(
    () => (data?.actions ?? []).slice(-12).reverse().map((a) => ({ id: a.id, summary: a.summary ?? "" })),
    [data],
  );
  const researchDocs = useMemo(
    () => (data?.research ?? []).map((r) => ({ filename: r.filename, title: r.title, date: r.date })),
    [data],
  );

  /* cross-tab research opening: palette dispatches an event ResearchView listens for */
  const openResearch = (filename: string) => {
    onNavigate("research");
    window.setTimeout(() => {
      window.dispatchEvent(new CustomEvent("mission:open-research", { detail: { filename } }));
    }, 120);
    onOpenChange(false);
  };

  /* jump to a row inside one of the list views: switch tab, then focus-flash
     the exact record (Evidence/Decisions/Earnings views listen for this) */
  const focusItem = (tab: TabValue, id: string) => {
    onNavigate(tab);
    window.setTimeout(() => {
      window.dispatchEvent(new CustomEvent("mission:focus-item", { detail: { id } }));
    }, 160);
    onOpenChange(false);
  };

  /* lock page scroll behind the dialog is handled by radix; nothing else to do */
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onOpenChange(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onOpenChange]);

  if (!data) return null;

  return (
    <CommandDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Mission command palette"
      description="Search mission state and run quick actions"
      className="border-zinc-800 bg-zinc-950 p-0 text-zinc-100 sm:max-w-xl"
    >
      <Command className="[&_[data-slot=command-input-wrapper]]:border-zinc-800/70 [&_[cmdk-group-heading]]:font-mono [&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-[0.14em] [&_[cmdk-group-heading]]:text-zinc-500">
        <CommandInput placeholder="Search mission state — ops, evidence, decisions, research…" className="text-zinc-100 placeholder:text-zinc-600" />
        <CommandList className="mission-scroll max-h-[26rem]">
          <CommandEmpty className="font-mono text-xs text-zinc-600">no matches in current mission state</CommandEmpty>

          {/* navigate */}
          <CommandGroup heading="Go to">
            {(
              [
                ["pipeline", "Pipeline", <Activity key="a" className="size-4 text-emerald-400" />],
                ["earnings", "Earnings", <HandCoins key="b" className="size-4 text-emerald-400" />],
                ["timeline", "Timeline", <History key="c" className="size-4 text-emerald-400" />],
                ["evidence", "Evidence Log", <ListChecks key="d" className="size-4 text-emerald-400" />],
                ["next", "Next Actions", <Route key="e" className="size-4 text-emerald-400" />],
                ["decisions", "Decisions", <Gavel key="f" className="size-4 text-violet-400" />],
                ["research", "Research", <BookOpen key="g" className="size-4 text-violet-400" />],
              ] as Array<[TabValue, string, React.ReactNode]>
            ).map(([value, label, icon]) => (
              <CommandItem
                key={value}
                value={`go ${label} tab ${value}`}
                onSelect={() => go(value)}
                className="gap-2.5 font-mono text-xs text-zinc-300 data-[selected=true]:bg-zinc-800/70 data-[selected=true]:text-zinc-100"
              >
                {icon}
                {label}
                <CommandShortcut className="font-mono text-[10px] text-zinc-600">
                  {String(TAB_ORDER.indexOf(value) + 1)}
                </CommandShortcut>
              </CommandItem>
            ))}
          </CommandGroup>

          <CommandSeparator className="bg-zinc-800/70" />

          {/* quick actions */}
          <CommandGroup heading="Quick actions">
            <CommandItem
              value="refresh now reload sync mission state"
              onSelect={() => {
                onRefresh();
                onOpenChange(false);
              }}
              className="gap-2.5 font-mono text-xs text-zinc-300 data-[selected=true]:bg-zinc-800/70 data-[selected=true]:text-zinc-100"
            >
              <RefreshCw className="size-4 text-emerald-400" />
              Refresh mission state now
              <CommandShortcut className="font-mono text-[10px] text-zinc-600">r</CommandShortcut>
            </CommandItem>
            <CommandItem
              value="copy evm receiving address wallet ethereum"
              onSelect={() => copy("EVM receiving address", data.state.wallets.evm)}
              className="gap-2.5 font-mono text-xs text-zinc-300 data-[selected=true]:bg-zinc-800/70 data-[selected=true]:text-zinc-100"
            >
              <ClipboardCopy className="size-4 text-zinc-400" />
              Copy EVM receiving address
            </CommandItem>
            {data.flagship && (
              <CommandItem
                value="copy x402 research brief endpoint url"
                onSelect={() => copy("Endpoint URL", `${data.flagship!.public_base}/api/x402/research-brief`)}
                className="gap-2.5 font-mono text-xs text-zinc-300 data-[selected=true]:bg-zinc-800/70 data-[selected=true]:text-zinc-100"
              >
                <ClipboardCopy className="size-4 text-zinc-400" />
                Copy x402 endpoint URL
              </CommandItem>
            )}
            {data.flagship && (
              <CommandItem
                value="open x402 endpoint 402 challenge external"
                onSelect={() => openExternal(`${data.flagship!.public_base}/api/x402/research-brief`)}
                className="gap-2.5 font-mono text-xs text-zinc-300 data-[selected=true]:bg-zinc-800/70 data-[selected=true]:text-zinc-100"
              >
                <ExternalLink className="size-4 text-zinc-400" />
                Open x402 endpoint (402 challenge)
              </CommandItem>
            )}
            {data.flagship && (
              <CommandItem
                value="open circle faucet test usdc claim"
                onSelect={() => openExternal("https://faucet.circle.com")}
                className="gap-2.5 font-mono text-xs text-zinc-300 data-[selected=true]:bg-zinc-800/70 data-[selected=true]:text-zinc-100"
              >
                <ArrowUpRight className="size-4 text-amber-400" />
                Open faucet.circle.com (the 30s unlock)
              </CommandItem>
            )}
            <CommandItem
              value="copy mission status report markdown summary export"
              onSelect={() => copy("Mission status report (markdown)", buildMissionReport(data))}
              className="gap-2.5 font-mono text-xs text-zinc-300 data-[selected=true]:bg-zinc-800/70 data-[selected=true]:text-zinc-100"
            >
              <ClipboardCopy className="size-4 text-emerald-400" />
              Copy status report (markdown)
              <CommandShortcut className="font-mono text-[10px] text-zinc-600">honest</CommandShortcut>
            </CommandItem>
            <CommandItem
              value="download mission report file markdown export save"
              onSelect={() => {
                downloadReport(data);
                toast({
                  title: "Mission report downloaded",
                  description: "Truthful markdown export of the live mission state",
                });
                onOpenChange(false);
              }}
              className="gap-2.5 font-mono text-xs text-zinc-300 data-[selected=true]:bg-zinc-800/70 data-[selected=true]:text-zinc-100"
            >
              <FileDown className="size-4 text-emerald-400" />
              Download mission report (.md)
            </CommandItem>
            <CommandItem
              value="print mission report paper paper-friendly save as pdf"
              onSelect={() => {
                onOpenChange(false);
                window.setTimeout(() => window.print(), 150);
              }}
              className="gap-2.5 font-mono text-xs text-zinc-300 data-[selected=true]:bg-zinc-800/70 data-[selected=true]:text-zinc-100"
            >
              <Printer className="size-4 text-zinc-400" />
              Print mission report
              <CommandShortcut className="font-mono text-[10px] text-zinc-600">p</CommandShortcut>
            </CommandItem>
          </CommandGroup>

          {/* opportunities */}
          {opportunities.length > 0 && (
            <>
              <CommandSeparator className="bg-zinc-800/70" />
              <CommandGroup heading={`Opportunities (${opportunities.length})`}>
                {opportunities.map((o) => (
                  <CommandItem
                    key={o.id}
                    value={`opp ${o.id} ${o.name} ${o.status} ${o.category}`}
                    onSelect={() => go("pipeline")}
                    className="gap-2.5 font-mono text-xs text-zinc-300 data-[selected=true]:bg-zinc-800/70 data-[selected=true]:text-zinc-100"
                  >
                    <Route className="size-4 shrink-0 text-zinc-500" />
                    <span className="min-w-0 truncate">
                      <span className="mr-1.5 text-[10px] text-zinc-600">{o.id}</span>
                      {o.name}
                    </span>
                    <CommandShortcut className="font-mono text-[9px] uppercase text-zinc-600">{o.status}</CommandShortcut>
                  </CommandItem>
                ))}
              </CommandGroup>
            </>
          )}

          {/* decisions */}
          {decisions.length > 0 && (
            <>
              <CommandSeparator className="bg-zinc-800/70" />
              <CommandGroup heading={`Decisions (${decisions.length})`}>
                {decisions.map((d) => (
                  <CommandItem
                    key={d.id}
                    value={`decision ${d.id} ${d.title}`}
                    onSelect={() => focusItem("decisions", d.id)}
                    className="gap-2.5 font-mono text-xs text-zinc-300 data-[selected=true]:bg-zinc-800/70 data-[selected=true]:text-zinc-100"
                  >
                    <Gavel className="size-4 shrink-0 text-violet-400/80" />
                    <span className="min-w-0 truncate">
                      <span className="mr-1.5 text-[10px] text-violet-400/80">{d.id}</span>
                      {d.title}
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </>
          )}

          {/* research */}
          {researchDocs.length > 0 && (
            <>
              <CommandSeparator className="bg-zinc-800/70" />
              <CommandGroup heading={`Research notes (${researchDocs.length})`}>
                {researchDocs.map((r) => (
                  <CommandItem
                    key={r.filename}
                    value={`research note ${r.title} ${r.date} ${r.filename}`}
                    onSelect={() => openResearch(r.filename)}
                    className="gap-2.5 font-mono text-xs text-zinc-300 data-[selected=true]:bg-zinc-800/70 data-[selected=true]:text-zinc-100"
                  >
                    <FileText className="size-4 shrink-0 text-violet-300/80" />
                    <span className="min-w-0 truncate">{r.title}</span>
                    <CommandShortcut className="font-mono text-[10px] text-zinc-600">{r.date}</CommandShortcut>
                  </CommandItem>
                ))}
              </CommandGroup>
            </>
          )}

          {/* recent evidence */}
          {recentActions.length > 0 && (
            <>
              <CommandSeparator className="bg-zinc-800/70" />
              <CommandGroup heading="Recent evidence">
                {recentActions.map((a) => (
                  <CommandItem
                    key={a.id}
                    value={`evidence action ${a.id} ${a.summary}`}
                    onSelect={() => focusItem("evidence", a.id)}
                    className="gap-2.5 text-xs text-zinc-300 data-[selected=true]:bg-zinc-800/70 data-[selected=true]:text-zinc-100"
                  >
                    <Check className="size-4 shrink-0 text-emerald-500/80" />
                    <span className="min-w-0 truncate font-mono text-[11px]">
                      <span className="mr-1.5 text-[10px] text-zinc-600">{a.id}</span>
                      {a.summary}
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </>
          )}

          {/* honest earnings reminder */}
          {data.earnings.length === 0 && (
            <>
              <CommandSeparator className="bg-zinc-800/70" />
              <CommandGroup heading="Status">
                <CommandItem
                  value="earnings zero honest none yet"
                  onSelect={() => go("earnings")}
                  className="gap-2.5 font-mono text-xs text-zinc-400 data-[selected=true]:bg-zinc-800/70"
                >
                  <Inbox className="size-4 text-zinc-500" />
                  Verified earnings: $0.00 — honest state, nothing fabricated
                </CommandItem>
              </CommandGroup>
            </>
          )}
        </CommandList>
      </Command>
    </CommandDialog>
  );
}

/** Header trigger button for the palette (also usable anywhere). */
export function PaletteTrigger({ onClick, hint }: { onClick: () => void; hint?: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Open command palette — search mission state"
      title="Command palette — search everything"
      className="flex items-center gap-1.5 rounded-md border border-zinc-700/60 bg-zinc-800/30 px-2 py-1.5 font-mono text-[10px] text-zinc-400 transition-colors hover:border-emerald-500/40 hover:text-zinc-200 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-emerald-500/60"
    >
      <Search className="size-3" aria-hidden="true" />
      <span className="hidden sm:inline">{hint ?? "Search"}</span>
      <kbd className="hidden rounded border border-zinc-700 bg-zinc-900 px-1 text-[9px] text-zinc-500 sm:inline">
        ⌘K
      </kbd>
    </button>
  );
}
